import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { FiChevronRight } from "react-icons/fi";

export type GlanceColor = "mist" | "sun" | "lilac" | "lime" | "rose" | "flame";

export interface GlanceItem {
  id: string;
  icon: ReactNode;
  color: GlanceColor;
  title: string;
  detail: string;
  /** Monto ya formateado, con signo y flecha si es un movimiento. */
  amount: string;
  to: string;
}

export interface GlanceListProps {
  title: string;
  items: GlanceItem[];
}

const COLOR_CLASS: Record<GlanceColor, string> = {
  mist: "bg-[var(--ds-card-mist)]",
  sun: "bg-[var(--ds-card-sun)]",
  lilac: "bg-[var(--ds-card-lilac)]",
  lime: "bg-[var(--ds-card-lime)]",
  rose: "bg-[var(--ds-card-rose)]",
  flame: "bg-[var(--ds-card-flame)]",
};

/** «Tu ciclo de un vistazo»: filas-enlace con icono en cuadro de color, dato clave y monto. Cada fila lleva a su pantalla. */
export function GlanceList({ title, items }: GlanceListProps) {
  return (
    <section aria-label={title} className="mt-2 flex flex-col gap-2">
      <h2 className="m-0 mb-3 text-[20px] font-medium leading-6 text-[var(--ds-ink)]">{title}</h2>
      {items.map((item) => (
        <Link
          key={item.id}
          to={item.to}
          className="flex min-h-18 items-center gap-3 rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] py-3 pl-3 pr-4 text-[var(--ds-ink)] no-underline"
        >
          <span className={`flex h-13 w-13 flex-none items-center justify-center rounded-[var(--ds-radius-md)] text-[var(--ds-on-card)] ${COLOR_CLASS[item.color]}`}>
            {item.icon}
          </span>
          <div className="min-w-0 flex-1">
            <b className="block truncate text-base font-medium leading-[22px]">{item.title}</b>
            <span className="block text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{item.detail}</span>
          </div>
          <div className="mr-2 whitespace-nowrap text-right text-base font-medium leading-[22px]">{item.amount}</div>
          <span aria-hidden="true" className="flex text-[var(--ds-ink-muted)]">
            <FiChevronRight aria-hidden="true" className="h-5 w-5" />
          </span>
        </Link>
      ))}
    </section>
  );
}
